<i18n>
{
	"en": {
		"title": "Cargo Carrier",
		"alternativeTitle": "Tractor",
		"failedToFetch": "Failed to fetch cargo carriers"
	},
	"no": {
		"title": "Lastebærer",
		"alternativeTitle": "Trekker",
		"failedToFetch": "Kunne ikke hente lastebærere"
	}
}
</i18n>
<template>
	<div>
		<t-combobox-button
			ref="comboboxButton"
			:title="isInputForLeg ? $t('alternativeTitle') : $t('title')"
			icon="geosearch"
			:limit="$options.SERVER_RETURN_LIMIT"

			:searchValue="searchValue"
			:items="filteredItems"
			:selected="value"

			:disabled="disabled"
			disableButtonTabbing

			@focus="onFocus"
			@input="onInput"
			@change="onChange"
			@blur="onBlur"
			@button:click="setShowDialog(true)"
		>
			<template #optionItemContent="slotProps">
				<div class="item">
					<div class="item-main-text">{{ slotProps.item.description }}</div>
					<div class="item-sub-text">{{ slotProps.item.registrationNumber }}</div>
				</div>
			</template>
		</t-combobox-button>

		<t-after-input :text="registrationNumber"/>

		<cargo-carriers-form-input-dialog
			:items="filteredCargoCarriers"
			:isShown="showDialog"
			:initialSearchValue="searchValue"
			icon="unknown-vehicle"
			@close="setShowDialog(false)"
			@ok="onChange"
		/>
	</div>
</template>

<script>
import { mapState, mapActions } from './store/cargoCarriers.store.js';
import formMixin from '@/components/evergreen/inputs/formMixin';
import popoverMixin from '@/components/evergreen/inputs/popoverMixin';
import CargoCarriersFormInputDialog from './CargoCarriersFormInput/CargoCarrierDialog';
import TAfterInput from '@/components/evergreen/timpex/inputs/TAfterInput';
import TComboboxButton from '@/components/evergreen/timpex/inputs/TComboboxButton';

export default {
	name: 'CargoCarriersInput',
	components: { TComboboxButton, TAfterInput, CargoCarriersFormInputDialog },
	mixins: [ formMixin, popoverMixin ],
	props: {
		value: Object,
		isInputForLeg: Boolean,
		disabled: {
			type: Boolean,
			default: false
		},
	},
	data() {
		return {
			showDialog: false,
			searchValue: '',
			focused: false,
		};
	},
	computed: {
		...mapState(['cargoCarriers']),
		filteredCargoCarriers() { // Filter out cargo carriers that cannot carry any load.
			if (this.isInputForLeg) {
				return this.cargoCarriers;
			} else {
				return this.cargoCarriers.filter(el => {
					if (el.cargoCarrierType && el.cargoCarrierType.code)
						return el.cargoCarrierType.code.includes('L');
					return false;
				});
			}
		},
		filteredItems() {
			let filter = (item) => {

				if (!item)
					return false;

				let loweredSearchValue = this.searchValue.toLowerCase();

				let ret = true;
				for (let c of loweredSearchValue.split(' ')) {
					ret = ret &&
						( (item.description && item.description.toLowerCase().includes(c))
							|| ( item.registrationNumber && item.registrationNumber.toString().toLowerCase().includes(c) )
						);
				}
				return ret;
			};

			if (this.isInputForLeg) {

				return this.cargoCarriers.filter(filter);

			} else {
				// Slightly confusing: The this.cargoCarriers variable contains non-cargo-carriers
				// Only those with cargoCarrierType.code.includes('L') are actual cargo-carriers (L = Lastebærer)
				let onlyActualCargoCarriers = this.cargoCarriers.filter(
					item => item
						&& item.cargoCarrierType
						&& item.cargoCarrierType.code
						&& item.cargoCarrierType.code.includes('L')
				);

				return onlyActualCargoCarriers.filter(filter);
			}

		},
		registrationNumber() {
			if (this.value && this.value.registrationNumber) {
				return this.value.registrationNumber;
			} else return '';
		},
	},
	watch: {
		value: {
			immediate: true,

			handler(value) {
				this.searchValue = this.getSearchValueFromValue(value);
			}
		}
	},
	async created() {
		try {
			await this.getCargoCarriers();
		} catch (e) {
			this.$notify({
				type: 'error',
				title: this.$t('failedToFetch')
			});
		}

	},
	methods: {
		...mapActions(['getCargoCarriers']),

		onFocus() {
			this.focused = true;
		},
		onInput(searchValue) {
			this.searchValue = searchValue;
		},
		onChange(event) {
			this.searchValue = this.getSearchValueFromValue(event);

			this.$emit('change', event);
		},
		onBlur(event) {

			this.focused = false;

			if (!isRelatedTargetButton(event, this.$refs.comboboxButton)) {

				if (this.searchValue === '')
					this.$emit('change', {});

				else
					this.searchValue = this.getSearchValueFromValue(this.value);
			}

			function isRelatedTargetButton(event, comboboxButtonRef) {

				return event.relatedTarget
					=== comboboxButtonRef.$refs.textInputButton.$refs.iconButton.$el;
			}
		},
		getSearchValueFromValue(value) {

			if (value && value.description && value.description.length)
				return value.description;
			else
				return '';
		},
		setShowDialog(showDialog) {

			if (this.showDialog === true && showDialog === false)
				this.searchValue = this.getSearchValueFromValue(this.value);

			this.showDialog = showDialog;
		},
	},
};
</script>

<style lang="scss" scoped>
@import '~@/components/evergreen/scss/colors';
@import '~@/components/evergreen/scss/text';

.item {
	display: flex;
	flex-direction: column;
	justify-content: center;
	height: 100%;
}

.item-main-text {
	height:16px;
	line-height: 16px;
	font-weight: 600;
	overflow: hidden;
	white-space: nowrap;
}

.item-sub-text {
	height:16px;
	font-size: 10px;
	font-weight: normal;
	font-style: normal;
	font-stretch: normal;
	line-height: 1.6;
	letter-spacing: normal;
	overflow: hidden;
	white-space: nowrap;
}
</style>
